import { ReactNode, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface TooltipProps {
  content: string | ReactNode;
  children: ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
  className?: string;
}

/**
 * Tooltip Component
 * 
 * A beautiful, accessible tooltip with smooth animations.
 * Appears on hover with configurable position and delay.
 * 
 * @example
 * <Tooltip content="Click to save your work" position="top">
 *   <button>Save</button>
 * </Tooltip>
 */
export function Tooltip({ 
  content, 
  children, 
  position = 'top', 
  delay = 200,
  className = ''
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    const id = setTimeout(() => {
      setIsVisible(true);
    }, delay);
    setTimeoutId(id);
  };

  const handleMouseLeave = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    setIsVisible(false);
  };

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  };

  const arrowClasses = {
    top: 'bottom-0 left-1/2 -translate-x-1/2 translate-y-full border-t-surface-800 border-t-8 border-x-transparent border-x-8 border-b-0',
    bottom: 'top-0 left-1/2 -translate-x-1/2 -translate-y-full border-b-surface-800 border-b-8 border-x-transparent border-x-8 border-t-0',
    left: 'right-0 top-1/2 -translate-y-1/2 translate-x-full border-l-surface-800 border-l-8 border-y-transparent border-y-8 border-r-0',
    right: 'left-0 top-1/2 -translate-y-1/2 -translate-x-full border-r-surface-800 border-r-8 border-y-transparent border-y-8 border-l-0',
  };

  return (
    <div 
      className="relative inline-flex"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className={`
              absolute z-50 px-3 py-2 text-sm font-medium text-khiimori-text-primary 
              bg-khiimori-bg-elevated rounded-lg shadow-lifted whitespace-nowrap
              pointer-events-none
              ${positionClasses[position]}
              ${className}
            `}
            style={{
              maxWidth: '300px',
            }}
          >
            {content}
            
            {/* Arrow */}
            <div className={`absolute w-0 h-0 ${arrowClasses[position]}`} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * Info Icon with Tooltip
 * 
 * A small info icon that shows a tooltip on hover.
 * Perfect for providing contextual help.
 * 
 * @example
 * <InfoTooltip content="This feature helps you organize tabs by topic" />
 */
export function InfoTooltip({ 
  content, 
  position = 'top' 
}: { 
  content: string | ReactNode; 
  position?: 'top' | 'bottom' | 'left' | 'right' 
}) {
  return (
    <Tooltip content={content} position={position}>
      <div className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-surface-200 hover:bg-khiimori-bg-tertiary transition-colors cursor-help">
        <svg 
          className="w-3 h-3 text-khiimori-text-muted" 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
          />
        </svg>
      </div>
    </Tooltip>
  );
}
